import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import { DeadLetterDetailModal } from './DeadLetterDetailModal';
import {
  useDeadLetterMessages,
  useRetryDeadLetter,
  useRemoveDeadLetter,
} from '../../application/hooks/useDeadLetterMessages';
import { useNotification } from '@/shared/contexts/useNotification';
import { DataTable } from '@/shared/components/ui/DataTable';

export const DeadLetterTab: React.FC = () => {
  const { showNotification } = useNotification();
  const { data: messages = [], isLoading, error: queryError } = useDeadLetterMessages();
  const retryMutation = useRetryDeadLetter();
  const removeMutation = useRemoveDeadLetter();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [removeConfirmId, setRemoveConfirmId] = useState<number | null>(null);

  const error =
    queryError instanceof Error ? queryError.message : queryError ? String(queryError) : null;

  const handleRowClick = (params: GridRowParams) => {
    setSelectedId((params.row as { id: number }).id);
    setDetailOpen(true);
  };

  const handleRetry = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    retryMutation.mutate(id, {
      onSuccess: () => showNotification('Message queued for retry', 'success'),
      onError: () => showNotification('Failed to retry message', 'error'),
    });
  };

  const handleRemove = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setRemoveConfirmId(id);
  };

  const confirmRemove = () => {
    if (removeConfirmId) {
      removeMutation.mutate(removeConfirmId, {
        onSuccess: () => {
          showNotification('Message removed from dead letters', 'success');
          setRemoveConfirmId(null);
        },
        onError: () => {
          showNotification('Failed to remove message', 'error');
          setRemoveConfirmId(null);
        },
      });
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'transport', headerName: 'Transport', width: 130 },
    {
      field: 'exceptionClass',
      headerName: 'Exception',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const parts = ((params.value as string) || '').split('\\');
        return (
          <Tooltip title={params.value as string}>
            <span>{parts[parts.length - 1]}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const id = (params.row as { id: number }).id;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title="Retry">
              <IconButton
                size="small"
                onClick={(e) => handleRetry(e, id)}
                disabled={retryMutation.isPending}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => handleRemove(e, id)}
                disabled={removeMutation.isPending}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <DataTable
        rows={messages}
        columns={columns}
        loading={retryMutation.isPending || removeMutation.isPending}
        emptyMessage="No dead letter messages"
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
          sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
        }}
        pageSizeOptions={[10, 25, 50]}
        onRowClick={handleRowClick}
        containerSx={{ mt: 2 }}
      />

      <DeadLetterDetailModal
        messageId={selectedId}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedId(null);
        }}
        onRetry={(id: number) => retryMutation.mutate(id)}
        onRemove={(id: number) => removeMutation.mutate(id)}
      />

      <Dialog open={removeConfirmId !== null} onClose={() => setRemoveConfirmId(null)}>
        <DialogTitle>Remove Message?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this message from dead letters? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmId(null)}>Cancel</Button>
          <Button onClick={confirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
